import React from "react";
import {
  AlertTriangle,
  Award,
  CheckCircle2,
  Info,
  Lightbulb,
  RefreshCw,
  Star,
  Target,
  TrendingUp,
  Zap,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import RatingMeter from "@/components/ui/RatingMeter";
import { cn } from "@/lib/utils";
import {
  clampRating,
  nextBandTarget,
  ratingBandInfo,
  ratingPercent,
  resolveScale,
} from "@/lib/ratingScale";

/* ---------------------------------------------------------------------------
 * Tone system
 *
 * Five tones, each earning its colour: `primary` for the brand/result, and the
 * four semantic states (positive / caution / critical / info). Every tone is a
 * tinted surface + matching border + icon chip, all derived from theme tokens
 * so both themes track automatically.
 *
 * `body` is deliberately absent from the coloured tones: --warning sits at 50%
 * lightness and fails contrast as text on a light surface, so paragraph copy
 * always stays on foreground / muted-foreground. Colour lives in the surface,
 * the border and the icon — never in the sentence.
 *
 * --success / --warning / --info exist in index.css but are not mapped in
 * tailwind.config.js, so they are referenced through arbitrary values. That
 * keeps the tokens authoritative without touching the global config (which
 * would restyle unrelated admin screens already using `bg-success` etc.).
 * ------------------------------------------------------------------------- */
const TONES = {
  primary: {
    surface: "border-primary/25 bg-primary/[0.07]",
    chip: "bg-primary/10 text-primary",
    value: "text-primary",
    rule: "border-l-primary",
  },
  positive: {
    surface: "border-[hsl(var(--success)/0.3)] bg-[hsl(var(--success)/0.08)]",
    chip: "bg-[hsl(var(--success)/0.14)] text-[hsl(var(--success))]",
    value: "text-[hsl(var(--success))]",
    rule: "border-l-[hsl(var(--success))]",
  },
  caution: {
    surface: "border-[hsl(var(--warning)/0.35)] bg-[hsl(var(--warning)/0.09)]",
    chip: "bg-[hsl(var(--warning)/0.16)] text-[hsl(var(--warning))]",
    value: "text-[hsl(var(--warning))]",
    rule: "border-l-[hsl(var(--warning))]",
  },
  critical: {
    surface: "border-destructive/30 bg-destructive/[0.08]",
    chip: "bg-destructive/[0.14] text-destructive",
    value: "text-destructive",
    rule: "border-l-destructive",
  },
  info: {
    surface: "border-[hsl(var(--info)/0.3)] bg-[hsl(var(--info)/0.08)]",
    chip: "bg-[hsl(var(--info)/0.14)] text-[hsl(var(--info))]",
    value: "text-[hsl(var(--info))]",
    rule: "border-l-[hsl(var(--info))]",
  },
  neutral: {
    surface: "border-border bg-muted/40",
    chip: "bg-background text-muted-foreground",
    value: "text-foreground",
    rule: "border-l-border",
  },
};

// The rating payload is assembled from several attendance sources, so a field
// can legitimately be absent. These keep a missing number from throwing inside
// `.toFixed()` — the original modal called it directly on raw values.
const num = (value, fallback = 0) =>
  Number.isFinite(Number(value)) ? Number(value) : fallback;

const fixed = (value, digits = 3) => num(value).toFixed(digits);

/** Section label — the icon carries the brand accent, the text stays muted. */
const SectionHeading = ({ icon: Icon, children }) => (
  <div className="mb-3 flex items-center gap-2">
    <Icon className="h-4 w-4 text-primary" aria-hidden="true" />
    <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
      {children}
    </h3>
  </div>
);

/** One line of the rating maths. `tone` tints the surface, icon and value. */
const CalculationRow = ({ icon: Icon, tone = "neutral", label, detail, value }) => {
  const t = TONES[tone];
  return (
    <div
      className={cn(
        "flex items-center justify-between gap-3 rounded-lg border p-3",
        t.surface,
      )}
    >
      <div className="flex min-w-0 items-center gap-3">
        <div
          className={cn(
            "flex h-9 w-9 shrink-0 items-center justify-center rounded-full",
            t.chip,
          )}
        >
          <Icon className="h-4 w-4" aria-hidden="true" />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-medium text-foreground">{label}</p>
          <p className="text-xs text-muted-foreground">{detail}</p>
        </div>
      </div>
      <p className={cn("shrink-0 text-lg font-semibold tabular-nums", t.value)}>
        {value}
      </p>
    </div>
  );
};

/** Advisory card. Tinted surface + left rule; the copy stays high-contrast. */
const TipCard = ({ icon: Icon, tone = "neutral", title, children }) => {
  const t = TONES[tone];
  return (
    <div className={cn("rounded-lg border border-l-[3px] p-4", t.surface, t.rule)}>
      <div className="flex items-start gap-3">
        <div
          className={cn(
            "flex h-7 w-7 shrink-0 items-center justify-center rounded-full",
            t.chip,
          )}
        >
          <Icon className="h-4 w-4" aria-hidden="true" />
        </div>
        <div className="min-w-0 space-y-1">
          <p className="text-sm font-semibold text-foreground">{title}</p>
          <div className="space-y-1 text-sm leading-relaxed text-muted-foreground">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

const StrategyCard = ({ index, title, children }) => (
  <div className="rounded-lg border border-border bg-muted/30 dark:bg-muted/60 p-4">
    <div className="flex items-start gap-3">
      <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
        {index}
      </div>
      <div className="min-w-0 space-y-1">
        <p className="text-sm font-semibold text-foreground">{title}</p>
        <p className="text-sm leading-relaxed text-muted-foreground">{children}</p>
      </div>
    </div>
  </div>
);

const LoadingState = () => (
  <div className="space-y-6" aria-hidden="true">
    <Skeleton className="h-40 w-full rounded-xl" />
    <div className="space-y-3">
      <Skeleton className="h-4 w-40" />
      {[0, 1, 2].map((row) => (
        <Skeleton key={row} className="h-16 w-full rounded-lg" />
      ))}
    </div>
    <div className="space-y-3">
      <Skeleton className="h-4 w-52" />
      {[0, 1].map((row) => (
        <Skeleton key={row} className="h-24 w-full rounded-lg" />
      ))}
    </div>
  </div>
);

const MessageState = ({ icon: Icon, tone = "neutral", title, description, action, role }) => (
  <div
    role={role}
    className="flex flex-col items-center gap-3 rounded-lg border border-dashed border-border px-6 py-12 text-center"
  >
    <div
      className={cn(
        "flex h-12 w-12 items-center justify-center rounded-full",
        TONES[tone].chip,
      )}
    >
      <Icon className="h-6 w-6" aria-hidden="true" />
    </div>
    <p className="font-semibold text-foreground">{title}</p>
    <p className="max-w-sm text-sm text-muted-foreground">{description}</p>
    {action}
  </div>
);

/**
 * Performance Rating Breakdown — a right-side drawer on desktop, full-width on
 * mobile.
 *
 * Purely presentational: `rating` and `otHours` are the values the dashboard
 * has already fetched, so opening the drawer issues no additional requests.
 */
const PerformanceRatingDrawer = ({
  open,
  onOpenChange,
  rating,
  otHours = 0,
  loading = false,
  error = "",
  onRetry,
}) => {
  const hours = num(otHours);
  // The configured scale — base AND cap. Everything scale-dependent below
  // derives from this, so there are no hardcoded 5s or 4.0s left here.
  // `resolveScale` reads base_rating off the payload directly.
  const scale = resolveScale(rating);
  const maxRating = scale.max;
  const info = rating ? ratingBandInfo(rating.rating, scale) : null;
  const band = info ? info.tone : "neutral";
  // The next band up and what it costs — replaces an invented "+0.5" target.
  const target = rating ? nextBandTarget(rating.rating, scale) : null;

  // rating_config.overtime is awarded PER 30-MINUTE BLOCK, not per hour — see
  // the OT approval path in attendance.controller.js and the admin settings
  // label "Overtime Reward Points (Per 30 Minutes)". This drawer previously
  // treated it as per-hour, so it understated every overtime figure by half and
  // told employees to log twice the hours they actually needed.
  const otPerBlock = num(rating?.overtime_bonus);
  const otPerHour = otPerBlock * 2;
  /** Mirrors the server: points = overtime x round(hours x 2). */
  const otPoints = (otHours) => otPerBlock * Math.round(num(otHours) * 2);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        // Full width on phones, progressively wider on desktop so the
        // breakdown rows and tip cards get room to breathe.
        className="flex w-full flex-col gap-0 border-l border-border bg-background p-0 shadow-2xl sm:max-w-lg md:max-w-xl lg:max-w-2xl"
      >
        {/* Header — stays fixed while the body scrolls. pr-12 keeps the title
            clear of the built-in close button. The Sheet is position:fixed, so
            it starts at the physical viewport top and ignores the body's
            safe-area padding — add the top inset here so the title clears the
            notch on iOS. Resolves to plain py-4 on devices with no notch. */}
        <SheetHeader
          className="shrink-0 space-y-1 border-b border-border px-5 py-4 pr-12 sm:px-6"
          style={{ paddingTop: "calc(1rem + var(--safe-area-inset-top))" }}
        >
          <SheetTitle className="flex items-center gap-2 text-lg font-semibold text-foreground">
            <Award className="h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
            Performance Rating Breakdown
          </SheetTitle>
          <SheetDescription>
            Understanding your rating and how to improve it
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-5 py-5 sm:px-6">
          {loading ? (
            <LoadingState />
          ) : error ? (
            <MessageState
              role="alert"
              tone="critical"
              icon={AlertTriangle}
              title="Could not load your rating"
              description={error}
              action={
                onRetry && (
                  <Button variant="outline" size="sm" onClick={onRetry}>
                    <RefreshCw className="mr-2 h-4 w-4" aria-hidden="true" />
                    Try again
                  </Button>
                )
              }
            />
          ) : !rating ? (
            <MessageState
              icon={TrendingUp}
              title="No rating data yet"
              description="Your performance rating appears once attendance has been recorded for this month."
            />
          ) : (
            <div className="space-y-8">
              {/* Current rating — the hero. A progress bar replaces the former
                  5-star row: stars are a fixed 5-point gauge, so they read as
                  "2 out of 5" for a 4.02/10 rating no matter how they are
                  scaled. A bar reads as "how far along", which stays true at
                  any configured maximum. */}
              <div className="rounded-xl border border-primary/25 bg-primary/[0.07] p-6">
                <div className="flex items-baseline justify-between gap-3">
                  <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    Your Current Rating
                  </p>
                  <span
                    className={cn(
                      "rounded-full px-2.5 py-0.5 text-xs font-semibold",
                      TONES[band].chip,
                    )}
                  >
                    {info.label}
                  </span>
                </div>

                <p className="mt-2 text-5xl font-bold tabular-nums text-primary">
                  {clampRating(rating.rating, scale).toFixed(3)}
                  <span className="ml-1 text-2xl font-medium text-muted-foreground">
                    / {maxRating}
                  </span>
                </p>

                <RatingMeter
                  rating={rating.rating}
                  scale={scale}
                  showValue={false}
                  showBand={false}
                  className="mt-4"
                />
                <p className="mt-2 text-xs text-muted-foreground">
                  {ratingPercent(rating.rating, scale).toFixed(0)}% of the
                  maximum achievable rating
                </p>
              </div>

              {/* Rating Calculation Breakdown */}
              <section>
                <SectionHeading icon={Target}>Rating Calculation</SectionHeading>
                <div className="space-y-2">
                  <CalculationRow
                    icon={Star}
                    label="Starting Rating"
                    // The old check compared against a hardcoded 4.0, which is
                    // just the legacy default base — so it read "Base rating"
                    // for 4/5 companies and "Carried from previous month" for
                    // every other configuration, regardless of what the value
                    // actually was.
                    detail={
                      rating.reset_annually
                        ? "Company base rating, reset each January"
                        : "Company base rating"
                    }
                    // scale.base rather than the raw field: getRatingData
                    // coalesces it with `|| null`, which would render blank.
                    value={fixed(scale.base)}
                  />

                  {rating.is_late_this_month && (
                    <CalculationRow
                      icon={AlertTriangle}
                      tone="critical"
                      label="Late Arrivals"
                      detail={`${rating.late_arrival_days} late arrival${
                        rating.late_arrival_days !== 1 ? "s" : ""
                      } × -${rating.late_arrival_penalty} points`}
                      value={`-${fixed(
                        num(rating.late_arrival_days) *
                          num(rating.late_arrival_penalty),
                      )}`}
                    />
                  )}

                  {hours > 0 && (
                    <CalculationRow
                      icon={Zap}
                      tone="positive"
                      label="Overtime Hours"
                      detail={`${hours} hour${hours !== 1 ? "s" : ""} × +${otPerHour} points`}
                      value={`+${fixed(otPoints(hours))}`}
                    />
                  )}

                  {/* Final Rating — the result, carried in the brand accent */}
                  <div className="flex items-center justify-between gap-3 rounded-lg border border-primary/30 bg-primary/10 p-4">
                    <p className="text-sm font-semibold text-foreground">
                      Final Rating
                    </p>
                    <p className="text-xl font-bold tabular-nums text-primary">
                      {fixed(rating.rating)}
                    </p>
                  </div>
                </div>
              </section>

              {/* Immediate Improvement Tips */}
              <section>
                <SectionHeading icon={Zap}>
                  Immediate Actions (This Month)
                </SectionHeading>
                <div className="grid gap-2">
                  {num(rating.late_arrival_days) > 0 ? (
                    <TipCard
                      icon={AlertTriangle}
                      tone="caution"
                      title="Improve Punctuality"
                    >
                      <p>
                        You have {rating.late_arrival_days} late arrival
                        {rating.late_arrival_days !== 1 ? "s" : ""} this month.
                        Each late arrival costs -{rating.late_arrival_penalty}{" "}
                        points.
                      </p>
                      <p className="font-medium text-foreground">
                        Punctuality bonus lost for this month. Start fresh next
                        month to earn +{rating.punctuality_bonus} bonus.
                      </p>
                    </TipCard>
                  ) : (
                    <TipCard
                      icon={CheckCircle2}
                      tone="positive"
                      title="Perfect Punctuality"
                    >
                      <p>
                        Keep up the excellent work — on track for{" "}
                        <span className="font-medium text-foreground">
                          +{rating.punctuality_bonus} bonus
                        </span>
                        , credited at month end.
                      </p>
                    </TipCard>
                  )}

                  <TipCard icon={Zap} tone="info" title="Log Overtime Hours">
                    <p>
                      Currently {hours} approved OT hour{hours !== 1 ? "s" : ""}.
                      Each hour adds +{otPerHour} to your rating.
                    </p>
                    <p className="font-medium text-foreground">
                      Potential: +10 OT hours this month = +
                      {otPoints(10)} rating boost.
                    </p>
                  </TipCard>

                  {target && (
                    <TipCard icon={Target} tone="info" title="Quick Win Calculation">
                      <p>
                        To reach{" "}
                        <span className="font-medium text-foreground">
                          {target.label}
                        </span>{" "}
                        ({target.threshold.toFixed(2)}) — {target.gap.toFixed(2)}{" "}
                        to go:
                      </p>
                      <ul className="ml-4 list-disc space-y-1 marker:text-muted-foreground/50">
                        <li>
                          Zero late arrivals → +{rating.punctuality_bonus} bonus
                          next month
                        </li>
                        <li>
                          +20 OT hours → +{otPoints(20)} rating
                        </li>
                        <li className="font-medium text-foreground">
                          Combined: +
                          {num(rating.punctuality_bonus) + otPoints(20)}{" "}
                          improvement possible
                        </li>
                      </ul>
                    </TipCard>
                  )}
                </div>
              </section>

              {/* Long-term Strategies — four peers, so they stay neutral;
                  colouring each one is what made the old modal noisy. */}
              <section>
                <SectionHeading icon={Lightbulb}>
                  Long-Term Strategies
                </SectionHeading>
                <div className="grid gap-2">
                  <StrategyCard index={1} title="Consistent Punctuality">
                    Arrive on time every day. The cumulative system carries your
                    rating forward, so maintaining punctuality month-over-month
                    compounds your rating growth. +{rating.punctuality_bonus}/month
                    × 12 months = +{fixed(num(rating.punctuality_bonus) * 12, 2)}{" "}
                    potential.
                  </StrategyCard>

                  <StrategyCard index={2} title="Strategic Overtime">
                    Take on meaningful OT when available. This compounds with
                    punctuality for steady rating growth throughout the year.
                  </StrategyCard>

                  <StrategyCard index={3} title="Avoid Rating Decay">
                    Late arrivals are cumulative penalties. Even one late arrival
                    loses you the punctuality bonus (-{rating.punctuality_bonus})
                    plus the penalty (-{rating.late_arrival_penalty}) ={" "}
                    {fixed(
                      -(
                        num(rating.punctuality_bonus) +
                        num(rating.late_arrival_penalty)
                      ),
                      2,
                    )}{" "}
                    impact. Consistency is key.
                  </StrategyCard>

                  <StrategyCard
                    index={4}
                    title={`Target ${maxRating} Rating`}
                  >
                    The system caps at {maxRating}. Maintain perfect
                    punctuality and consistent OT to reach and stay at maximum
                    rating. This demonstrates sustained excellence and dedication.
                  </StrategyCard>
                </div>
              </section>

              {/* Rating System Info — reference material, intentionally quiet */}
              <section>
                <SectionHeading icon={Info}>
                  How the Rating System Works
                </SectionHeading>
                <dl className="divide-y divide-border overflow-hidden rounded-lg border border-border bg-muted/30 dark:bg-muted/60 text-sm">
                  {[
                    [
                      "Cumulative System",
                      "Your rating carries forward month-to-month throughout the year",
                    ],
                    [
                      "Base Rating",
                      rating.reset_annually
                        ? `Everyone starts each year on a rating of ${scale.base.toFixed(
                            3,
                          )}, then carries forward month to month`
                        : `Everyone starts on a rating of ${scale.base.toFixed(
                            3,
                          )}, then carries forward month to month`,
                    ],
                    [
                      "Late Arrivals",
                      `-${rating.late_arrival_penalty} per occurrence (immediate penalty)`,
                    ],
                    [
                      "Overtime",
                      `+${otPerBlock} per 30 minutes of approved overtime (+${otPerHour} per hour)`,
                    ],
                    [
                      "Punctuality Bonus",
                      `+${rating.punctuality_bonus} if zero late arrivals in the month (credited at month end only)`,
                    ],
                    [
                      "Maximum Rating",
                      `Capped at ${maxRating} to ensure fairness`,
                    ],
                    // Spells out where the bands actually sit, so the badge at
                    // the top of the drawer is never an unexplained verdict.
                    [
                      "Performance Bands",
                      `Good from ${scale.base} (the base rating), Excellent from ` +
                        `${scale.thresholds.excellent.toFixed(2)}, Outstanding from ` +
                        `${scale.thresholds.outstanding.toFixed(2)}`,
                    ],
                    [
                      "Annual Reset",
                      rating.reset_annually
                        ? `Your rating returns to ${scale.base.toFixed(3)} each 1 January`
                        : `Your rating is never reset — it carries forward indefinitely`,
                    ],
                  ].map(([term, description]) => (
                    <div
                      key={term}
                      className="flex flex-col gap-0.5 px-4 py-2.5 sm:flex-row sm:gap-4"
                    >
                      <dt className="shrink-0 font-medium text-foreground sm:w-40">
                        {term}
                      </dt>
                      <dd className="text-muted-foreground">{description}</dd>
                    </div>
                  ))}
                </dl>
              </section>
            </div>
          )}
        </div>

        <SheetFooter className="shrink-0 border-t border-border px-5 py-3 sm:px-6">
          <SheetClose asChild>
            <Button variant="outline">Close</Button>
          </SheetClose>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

export default PerformanceRatingDrawer;
